/**
 * CognitoAuthService
 *
 * Implements the OAuth2 PKCE flow directly against the Cognito Hosted UI.
 * The PRO frontend initiates the redirect, receives the `?code=` callback,
 * and exchanges it for tokens without an intermediary auth-ui.
 *
 * On localhost the Cognito config may be absent; callers should guard with
 * `isConfigured()` before calling `initiateLogin()`.
 *
 * Config is read from RuntimeConfig.cognito (nested object), which is
 * populated by /assets/config.json at runtime.
 */

import { Injectable, inject } from '@angular/core';
import { ConfigService, CognitoRuntimeConfig } from '@tsin-core/services/common/config.service';

@Injectable({ providedIn: 'root' })
export class CognitoAuthService {
  private config = inject(ConfigService);

  /** True when the runtime config contains a fully populated Cognito block. */
  isConfigured(): boolean {
    const cog = this.config.getCognitoConfig();
    return !!(cog?.clientId && cog?.domain && cog?.redirectSignIn);
  }

  /**
   * Redirect the browser to the Cognito Hosted UI for PKCE login.
   * Generates a code verifier/challenge and stores the verifier in
   * sessionStorage for use in the callback.
   *
   * @param returnPath  App path to navigate to after successful login (default '/').
   */
  async initiateLogin(returnPath = '/'): Promise<void> {
    const cog = this.requireCognitoConfig();
    const hostedUiUrl = this.hostedUiUrl(cog);

    const verifier = this.generateCodeVerifier();
    const challenge = await this.generateCodeChallenge(verifier);

    sessionStorage.setItem('pkce_verifier', verifier);
    sessionStorage.setItem('pkce_return_path', returnPath);

    const params = new URLSearchParams({
      response_type: 'code',
      client_id: cog.clientId,
      redirect_uri: cog.redirectSignIn,
      scope: 'openid email profile',
      code_challenge: challenge,
      code_challenge_method: 'S256',
    });

    window.location.href = `${hostedUiUrl}/oauth2/authorize?${params}`;
  }

  /**
   * Exchange the authorization code returned by Cognito for tokens.
   * Call this from the /auth/callback route handler when `?code=` is present.
   */
  async exchangeCodeForTokens(code: string): Promise<{
    accessToken: string;
    idToken: string;
    refreshToken: string;
    expiresIn: number;
  }> {
    const cog = this.requireCognitoConfig();
    const hostedUiUrl = this.hostedUiUrl(cog);

    const verifier = sessionStorage.getItem('pkce_verifier');
    if (!verifier) {
      throw new Error('PKCE verifier missing from sessionStorage — possible replay or session loss');
    }

    const body = new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: cog.clientId,
      code,
      redirect_uri: cog.redirectSignIn,
      code_verifier: verifier,
    });

    const response = await fetch(`${hostedUiUrl}/oauth2/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    });

    if (!response.ok) {
      const errText = await response.text();
      throw new Error(`Cognito token exchange failed (${response.status}): ${errText}`);
    }

    sessionStorage.removeItem('pkce_verifier');

    const data = await response.json();
    return {
      accessToken: data.access_token,
      idToken: data.id_token,
      refreshToken: data.refresh_token,
      expiresIn: data.expires_in,
    };
  }

  /**
   * Use a Cognito refresh token to obtain a new access + id token silently.
   */
  async refreshTokens(refreshToken: string): Promise<{
    accessToken: string;
    idToken: string;
    expiresIn: number;
  }> {
    const cog = this.requireCognitoConfig();
    const hostedUiUrl = this.hostedUiUrl(cog);

    const body = new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: cog.clientId,
      refresh_token: refreshToken,
    });

    const response = await fetch(`${hostedUiUrl}/oauth2/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    });

    if (!response.ok) {
      throw new Error(`Cognito token refresh failed (${response.status})`);
    }

    const data = await response.json();
    return {
      accessToken: data.access_token,
      idToken: data.id_token,
      expiresIn: data.expires_in,
    };
  }

  /**
   * Redirect to the Cognito logout endpoint to terminate the SSO session.
   * The browser will land on `redirectSignOut` after the session is cleared.
   */
  logout(): void {
    const cog = this.config.getCognitoConfig();
    if (!cog?.clientId || !cog?.domain || !cog?.redirectSignOut) {
      console.warn('CognitoAuthService.logout: config incomplete, skipping Cognito logout');
      return;
    }
    const params = new URLSearchParams({
      client_id: cog.clientId,
      logout_uri: cog.redirectSignOut,
    });
    window.location.href = `${this.hostedUiUrl(cog)}/logout?${params}`;
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  private requireCognitoConfig(): CognitoRuntimeConfig {
    const cog = this.config.getCognitoConfig();
    if (!cog?.clientId || !cog?.domain || !cog?.redirectSignIn) {
      throw new Error('Cognito PKCE config is not available in runtime config');
    }
    return cog;
  }

  /** Build the Cognito Hosted UI base URL from the domain field. */
  private hostedUiUrl(cog: CognitoRuntimeConfig): string {
    return cog.domain.startsWith('https://') ? cog.domain : `https://${cog.domain}`;
  }

  private generateCodeVerifier(): string {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return btoa(String.fromCharCode(...array))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  }

  private async generateCodeChallenge(verifier: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const digest = await crypto.subtle.digest('SHA-256', data);
    return btoa(String.fromCharCode(...new Uint8Array(digest)))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  }
}
